import { Hash, FileText } from 'lucide-react';
import { memo, useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import DiscountLegend from '@/pages/PurchasesContainer/RequestPerOffer/components/PrepareRequest/itemsTable/columns/DiscountCol/DiscountLegend';

import type {
  IRequestType,
  ISuppliars,
  ISuppliarWithDebt,
} from '../../api/HandRequest.interface';
import { RequestTypeArabic } from '../../api/HandRequest.interface';

import { DebtBadge } from './DebtBadge';
import { AutoCompleteSuppliars } from './SuppliarsSelect';

// Memoize sub-components to prevent unnecessary renders
const MemoizedDebtBadge = memo(DebtBadge);
const MemoizedAutoCompleteSuppliars = memo(AutoCompleteSuppliars);

// Left side component with title and badge
const TitleSection = memo(
  ({
    requestTypeArabic,
    isEditMode,
    requestID,
  }: {
    requestTypeArabic: string;
    isEditMode?: boolean;
    requestID?: string;
  }) => (
    <div className="flex items-center gap-2">
      <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 shadow-sm dark:bg-primary/20">
        <FileText className="h-5 w-5 text-primary" strokeWidth={1.5} />
      </div>
      <div>
        <div className="flex items-center gap-1.5">
          <h2 className="text-base font-medium text-foreground">
            {isEditMode ? `تعديل طلبية شراء: ${requestID}` : 'طلبية شراء'}
          </h2>
          <Badge
            variant="outline"
            className={`rounded-md ${isEditMode ? 'border-amber-600/40 bg-amber-500/20 text-amber-700 dark:border-amber-500/50 dark:bg-amber-500/25 dark:text-amber-300' : 'border-primary/20 bg-primary/5 text-primary dark:bg-primary/10'} px-1.5 py-0 text-xs`}
          >
            {requestTypeArabic}
          </Badge>
        </div>

        <DiscountLegend />
      </div>
    </div>
  ),
);

TitleSection.displayName = 'TitleSection';

// Center component with supplier selection
const SupplierSelection = memo(
  ({
    suppliars,
    selectedSuppliar,
    handleSelectedSuppliar,
  }: {
    suppliars: ISuppliars[] | undefined;
    selectedSuppliar: ISuppliarWithDebt | null;
    handleSelectedSuppliar: (value: ISuppliars | null) => void;
  }) => (
    <div className="order-first flex w-full items-center gap-2 sm:order-none sm:w-auto sm:max-w-xs sm:flex-1 md:max-w-sm">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted/50 shadow-sm dark:bg-muted/30">
        <Hash className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <div className="flex-1">
        <MemoizedAutoCompleteSuppliars
          options={suppliars || []}
          searchPlaceholder="ابحث عن مورد"
          value={selectedSuppliar}
          onValueChange={handleSelectedSuppliar}
        />
      </div>
    </div>
  ),
);

SupplierSelection.displayName = 'SupplierSelection';

// Right side component with debt badge and reset button
const ControlsSection = memo(
  ({
    supplier,
    handleReset,
  }: {
    supplier: ISuppliarWithDebt | null;
    handleReset: () => void;
  }) => (
    <div className="flex items-center gap-3">
      <MemoizedDebtBadge supplier={supplier} />
      <Button
        onClick={handleReset}
        size="sm"
        variant="destructive"
        className="h-8 whitespace-nowrap px-3 shadow-sm transition-all hover:shadow"
      >
        الغاء
      </Button>
    </div>
  ),
);

ControlsSection.displayName = 'ControlsSection';

type HeaderRequestProps = {
  handleReset: () => void;
  requestType: IRequestType;
  selectedSuppliar: ISuppliarWithDebt | null;
  handleSelectedSuppliar: (value: ISuppliars | null) => void;
  suppliars: ISuppliars[] | undefined;
  isEditMode?: boolean;
  requestID?: string;
};

function HeaderRequest({
  handleReset,
  requestType,
  selectedSuppliar,
  handleSelectedSuppliar,
  suppliars,
  isEditMode,
  requestID,
}: HeaderRequestProps) {
  // Memoize the translated request type to prevent unnecessary recalculations
  const requestTypeArabic = useMemo(
    () => RequestTypeArabic[requestType],
    [requestType],
  );

  return (
    <Card
      className={`mb-4 border ${isEditMode ? 'border-amber-300/50 bg-gradient-to-r from-amber-100 via-amber-50/70 to-background shadow-md shadow-amber-200/30 dark:border-amber-700/40 dark:from-amber-800/40 dark:via-amber-800/20 dark:to-background/95 dark:shadow-amber-900/20' : 'border-border/20 bg-gradient-to-r from-background/90 to-background dark:from-background/60 dark:to-background/80'} p-2 shadow-sm`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <TitleSection
          requestTypeArabic={requestTypeArabic}
          isEditMode={isEditMode}
          requestID={requestID}
        />
        <SupplierSelection
          suppliars={suppliars}
          selectedSuppliar={selectedSuppliar}
          handleSelectedSuppliar={handleSelectedSuppliar}
        />
        <ControlsSection
          supplier={selectedSuppliar}
          handleReset={handleReset}
        />
      </div>
    </Card>
  );
}

// Export the component wrapped with memo to prevent unnecessary renders
export default memo(HeaderRequest);
